import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Plus,
  RefreshCw,
  X,
} from 'lucide-react';
import {
  RESERVATION_STATUTS,
  createReservationSchema,
  type ReservationStatut,
  type CreateReservationInput,
} from '@mygmao/shared';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { api, extractErrorMessage } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { ModalPortal } from '../components/ModalPortal';

/**
 * Page `/batiments/reservations` — Centre des réservations (US-BAT-05).
 *
 *  - Liste filtrée + pagination
 *  - Modal création (sélection salle réservable + date_debut/date_fin + récurrence)
 *  - Workflow validation/refus pour les responsables
 *  - Annulation par le demandeur ou un admin
 */

interface ReservationRow {
  id: string;
  salle_id: string;
  objet: string;
  date_debut: string;
  date_fin: string;
  statut: ReservationStatut;
  validateur_id: string | null;
  date_validation: string | null;
  motif_refus: string | null;
  salle: { id: string; code: string; nom: string };
  utilisateur: { id: string; first_name: string; last_name: string };
  service: { id: string; nom_complet: string } | null;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export function ReservationsPage() {
  const [items, setItems] = useState<ReservationRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [statutFilter, setStatutFilter] = useState<ReservationStatut | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [validating, setValidating] = useState<ReservationRow | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statutFilter, from, to, page]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (statutFilter) params.set('statut', statutFilter);
      if (from) params.set('from', new Date(from).toISOString());
      if (to) params.set('to', new Date(to).toISOString());
      params.set('page', String(page));
      params.set('page_size', '25');
      const res = await api.get<{ data: ReservationRow[]; pagination: Pagination }>(
        `/reservations?${params}`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel(reservation: ReservationRow) {
    if (!confirm(`Annuler la réservation "${reservation.objet}" ?`)) return;
    try {
      await api.post(`/reservations/${reservation.id}/cancel`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/batiments"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-1"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour aux bâtiments
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Calendar className="w-7 h-7 mr-2 text-primary-600" />
            Réservations de salles
          </h1>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle réservation
        </button>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex items-center flex-wrap gap-2">
        <select
          value={statutFilter}
          onChange={(e) => setStatutFilter(e.target.value as ReservationStatut | '')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Tous statuts</option>
          {RESERVATION_STATUTS.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <input
          type="datetime-local"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
          aria-label="Depuis"
        />
        <input
          type="datetime-local"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
          aria-label="Jusqu'à"
        />
        <button
          onClick={refresh}
          className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          title="Rafraîchir"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Objet
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Salle
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Période
                </th>
                <th className="hidden md:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Demandeur
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucune réservation.
                  </td>
                </tr>
              ) : (
                items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900">{r.objet}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {r.salle.code}
                      <span className="text-gray-400 ml-1">— {r.salle.nom}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700">
                      <div>{new Date(r.date_debut).toLocaleString()}</div>
                      <div className="text-gray-400">→ {new Date(r.date_fin).toLocaleString()}</div>
                    </td>
                    <td className="hidden md:table-cell px-3 py-2 text-gray-700">
                      {r.utilisateur.first_name} {r.utilisateur.last_name}
                    </td>
                    <td className="px-3 py-2">
                      <StatutBadge statut={r.statut} />
                      {r.motif_refus && (
                        <p className="text-xs text-red-600 mt-1">{r.motif_refus}</p>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {(r.statut === 'demande' || r.statut === 'en_attente') && (
                        <button
                          onClick={() => setValidating(r)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium border border-primary-200 text-primary-700 bg-white rounded hover:bg-primary-50 mr-1"
                          title="Valider / refuser"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {r.statut !== 'annulee' && (
                        <button
                          onClick={() => handleCancel(r)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium border border-red-200 text-red-600 bg-white rounded hover:bg-red-50"
                          title="Annuler"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
            <span className="text-gray-600">
              {pagination.total} réservation(s) — page {pagination.page}/{pagination.total_pages}
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
              </button>
              <button
                disabled={page >= pagination.total_pages}
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <CreateReservationModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            refresh();
          }}
        />
      )}

      {validating && (
        <ValidateReservationModal
          reservation={validating}
          onClose={() => setValidating(null)}
          onSaved={() => {
            setValidating(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create modal
// ─────────────────────────────────────────────────────────────────────────────

interface SalleOption {
  id: string;
  code: string;
  nom: string;
}

// Jours de la semaine côté backend : 0=dim … 6=sam (cf. schema ReservationSerie).
const JOURS_SEMAINE = [
  { value: 1, label: 'Lu' },
  { value: 2, label: 'Ma' },
  { value: 3, label: 'Me' },
  { value: 4, label: 'Je' },
  { value: 5, label: 'Ve' },
  { value: 6, label: 'Sa' },
  { value: 0, label: 'Di' },
];

function CreateReservationModal({ onClose, onSaved }: { onClose(): void; onSaved(): void }) {
  const [sallesReservables, setSallesReservables] = useState<SalleOption[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [enableRecurrence, setEnableRecurrence] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateReservationInput>({
    resolver: zodResolver(createReservationSchema) as unknown as Resolver<CreateReservationInput>,
  });

  const typeRecurrence = watch('recurrence.type_recurrence');

  function toggleDay(value: number) {
    setSelectedDays((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value].sort(),
    );
  }

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: SalleOption[] }>(
          '/salles?est_reservable=true&page_size=200',
        );
        setSallesReservables(res.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      }
    })();
  }, []);

  async function onSubmit(values: CreateReservationInput) {
    setErrorMessage(null);
    try {
      const payload: CreateReservationInput = {
        ...values,
        date_debut: new Date(values.date_debut).toISOString(),
        date_fin: new Date(values.date_fin).toISOString(),
      };
      if (!enableRecurrence) {
        // Pas de récurrence — on retire le champ pour ne pas le valider.
        delete (payload as Partial<CreateReservationInput>).recurrence;
      } else if (payload.recurrence) {
        // Injecte les jours de la semaine sélectionnés (uniquement
        // pertinent pour la récurrence hebdomadaire).
        payload.recurrence = {
          ...payload.recurrence,
          jours_semaine: payload.recurrence.type_recurrence === 'hebdomadaire' ? selectedDays : [],
        };
      }
      await api.post('/reservations', payload);
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Nouvelle réservation</h2>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <Field label="Salle *" error={errors.salle_id?.message}>
              <select {...register('salle_id')} className={inputClass} defaultValue="">
                <option value="" disabled>
                  — Choisir une salle réservable —
                </option>
                {sallesReservables.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} — {s.nom}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Objet *" error={errors.objet?.message}>
              <input
                {...register('objet')}
                placeholder="ex. Réunion équipe maintenance"
                className={inputClass}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Date début *" error={errors.date_debut?.message}>
                <input type="datetime-local" {...register('date_debut')} className={inputClass} />
              </Field>
              <Field label="Date fin *" error={errors.date_fin?.message}>
                <input type="datetime-local" {...register('date_fin')} className={inputClass} />
              </Field>
            </div>

            <label className="flex items-center cursor-pointer pt-2 border-t border-gray-100">
              <input
                type="checkbox"
                checked={enableRecurrence}
                onChange={(e) => setEnableRecurrence(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Activer la récurrence</span>
            </label>

            {enableRecurrence && (
              <div className="space-y-2 pl-6">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Type">
                    <select {...register('recurrence.type_recurrence')} className={inputClass}>
                      <option value="quotidienne">Quotidienne</option>
                      <option value="hebdomadaire">Hebdomadaire</option>
                      <option value="mensuelle">Mensuelle</option>
                    </select>
                  </Field>
                  <Field label="Fréquence">
                    <input
                      type="number"
                      min="1"
                      defaultValue={1}
                      {...register('recurrence.frequence', { valueAsNumber: true })}
                      className={inputClass}
                    />
                  </Field>
                </div>
                <Field label="Date fin récurrence *">
                  <input
                    type="date"
                    {...register('recurrence.date_fin_recurrence')}
                    className={inputClass}
                  />
                </Field>

                {typeRecurrence === 'hebdomadaire' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Jours de la semaine
                    </label>
                    <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                      {JOURS_SEMAINE.map((j) => {
                        const active = selectedDays.includes(j.value);
                        return (
                          <button
                            key={j.value}
                            type="button"
                            onClick={() => toggleDay(j.value)}
                            className={`px-3 py-1.5 text-xs font-medium border-r border-gray-200 last:border-r-0 ${
                              active
                                ? 'bg-primary-500 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {j.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Cliquez sur les jours souhaités. Si aucun n'est sélectionné, la récurrence
                      suit la date de début sans filtre.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Créer
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Validate modal
// ─────────────────────────────────────────────────────────────────────────────

function ValidateReservationModal({
  reservation,
  onClose,
  onSaved,
}: {
  reservation: ReservationRow;
  onClose(): void;
  onSaved(): void;
}) {
  const [motif, setMotif] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function decide(approve: boolean) {
    setBusy(true);
    setErrorMessage(null);
    try {
      await api.post(`/reservations/${reservation.id}/validate`, {
        approve,
        motif_refus: approve ? null : motif || null,
      });
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Valider la réservation</h2>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div className="rounded-lg bg-gray-50 p-3 text-sm">
              <p className="font-medium text-gray-900">{reservation.objet}</p>
              <p className="text-gray-600 mt-1 text-xs">
                {reservation.salle.code} — {reservation.salle.nom}
                <br />
                <Clock className="inline w-3 h-3 mr-1" />
                {new Date(reservation.date_debut).toLocaleString()} →{' '}
                {new Date(reservation.date_fin).toLocaleString()}
                <br />
                Demandé par {reservation.utilisateur.first_name} {reservation.utilisateur.last_name}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motif (en cas de refus)
              </label>
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={() => decide(false)}
                disabled={busy}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Refuser
              </button>
              <button
                type="button"
                onClick={() => decide(true)}
                disabled={busy}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Valider
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Badges & helpers
// ─────────────────────────────────────────────────────────────────────────────

function StatutBadge({ statut }: { statut: ReservationStatut }) {
  const cfg: Record<ReservationStatut, string> = {
    demande: 'bg-blue-100 text-blue-800 border-blue-200',
    en_attente: 'bg-amber-100 text-amber-800 border-amber-200',
    validee: 'bg-green-100 text-green-800 border-green-200',
    confirmee: 'bg-green-100 text-green-800 border-green-200',
    annulee: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg[statut]}`}
    >
      {statut.replace(/_/g, ' ')}
    </span>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// Suppress unused warning — useAuth might be useful for permission checks later.
void useAuth;
